import { useCallback, useEffect, useRef, useState } from "react"

import { Box, Slide, Stack } from "@mui/material"
import dayjs from "dayjs"
import { useLocalStorage } from "usehooks-ts"

import { timeFromNow } from "@ggs/utils"
import { Logo, type FC } from ".."
import useIsomorphicLayoutEffect from "../../helpers/useIsomorphicLayoutEffect"

const PageLoad: FC = () => {
  const [pageLoadExpires, setPageLoadExpires] = useLocalStorage("pageLoadExpires", "")
  const [showPageLoad, setShowPageLoad] = useState(
    !!pageLoadExpires ? timeFromNow(pageLoadExpires, "hour", true) > 1 : true
  )
  const [animationFinished, setAnimationFinished] = useState(false)
  const [pageReady, setPageReady] = useState(false)

  const barRef = useRef<HTMLDivElement>(null)

  const onReady = useCallback(() => {
    setPageReady(true)
  }, [])

  const onTransitionEnd = useCallback(() => {
    // Only set the page load expires if we are not in storybook
    if (process.env.STORYBOOK !== "yes") setPageLoadExpires(dayjs().add(1, "hour").toISOString())

    setAnimationFinished(true)
  }, [])

  // when the page is ready, set the flag to close the animation
  useEffect(() => {
    document.addEventListener("DOMContentLoaded", onReady)
    if (document.readyState === "complete") onReady()
    return () => document.removeEventListener("DOMContentLoaded", onReady)
  }, [])

  // if page is ready and animation is finished, close the page load
  useEffect(() => {
    if (pageReady && animationFinished) setShowPageLoad(false)
  }, [pageReady, animationFinished])

  // start the animation
  useIsomorphicLayoutEffect(() => {
    if (barRef.current) {
      const body = document.body
      if (body) {
        body.style.overflowY = "scroll"
        body.style.position = "fixed"
        body.style.width = "100%"
      }
      setTimeout(() => {
        barRef.current!.style.transform = "translateY(-50%) scaleY(0)"
      }, 100)
    }
  }, [barRef])

  return (
    <Slide
      in={showPageLoad}
      direction="down"
      appear={false}
      timeout={500}
      onExited={() => {
        const body = document.body
        if (body) {
          body.style.removeProperty("overflowY")
          body.style.removeProperty("position")
          body.style.removeProperty("width")
        }
      }}
      unmountOnExit
    >
      <Stack
        alignItems="center"
        justifyContent="center"
        bgcolor={(theme) => theme.palette["primary.800"].main}
        position="fixed"
        top={0}
        left={0}
        bottom={0}
        right={0}
        overflow="hidden"
        zIndex={999999}
      >
        <Logo
          size={{ height: { xs: 22, md: 42 }, width: { xs: 84, md: 160 } }}
          sx={{ fill: (theme) => theme.palette["tertiary.800"].main }}
        />
        <Box
          ref={barRef}
          sx={{
            bgcolor: (theme) => theme.palette["primary.800"].main,
            position: "absolute",
            height: 64,
            top: "50%",
            left: 0,
            right: 0,
            width: "100%",
            transform: "translateY(-50%) scaleY(1)",
            transformOrigin: "top center",
            transition: (theme) =>
              theme.transitions.create(["transform"], {
                duration: 500,
                easing: "ease-in",
              }),
          }}
          onTransitionEnd={() => setTimeout(onTransitionEnd, 500)}
        />
      </Stack>
    </Slide>
  )
}
export default PageLoad
